/**
 * Resolves popup field overrides for a marker based on enabled custom layers.
 *
 * Precedence among matching layers:
 *   1. Lowest displayOrder (if present)
 *   2. Earliest createdAt
 *   3. Lexical id (tie-breaker)
 */

import { compileLayerPredicate } from './discoverLayerPredicates';

const STANDARD_FIELD_MAP = Object.freeze({
    author: 'showAuthor',
    visibility: 'showVisibility',
    created: 'showCreated',
});

const METADATA_PREFIX = 'metadata.';

function layerPrecedenceComparator(a, b) {
    const orderA = typeof a.displayOrder === 'number' ? a.displayOrder : Infinity;
    const orderB = typeof b.displayOrder === 'number' ? b.displayOrder : Infinity;
    if (orderA !== orderB) return orderA - orderB;

    const createdA = a.createdAt || '';
    const createdB = b.createdAt || '';
    if (createdA !== createdB) return createdA < createdB ? -1 : 1;

    return (a.id || '').localeCompare(b.id || '');
}

function buildOverridesFromPopup(popup) {
    if (!popup || typeof popup !== 'object') return null;

    const fields = Array.isArray(popup.fields) ? popup.fields : [];
    if (fields.length === 0) return null;

    const overrides = {
        showAuthor: false,
        showVisibility: false,
        showCreated: false,
        priorityMetadataFields: [],
        hideEmpty: typeof popup.hideEmpty === 'boolean' ? popup.hideEmpty : false,
    };

    fields.forEach((field) => {
        const name = typeof field === 'string' ? field : field?.name;
        if (typeof name !== 'string') return;

        const standardFlag = STANDARD_FIELD_MAP[name];
        if (standardFlag) {
            overrides[standardFlag] = true;
            return;
        }

        if (name.startsWith(METADATA_PREFIX)) {
            const metaKey = name.slice(METADATA_PREFIX.length);
            if (metaKey) {
                overrides.priorityMetadataFields.push(metaKey);
            }
        }
    });

    return overrides;
}

/**
 * For a single marker, find the highest-precedence enabled custom layer
 * that matches the marker's type and has a popup schema, then return
 * the derived popup overrides (or null).
 *
 * @param {object} marker
 * @param {Array} candidates - pre-sorted, pre-filtered layers with compiled predicates
 */
function resolvePopupOverridesFromCandidates(marker, candidates) {
    const markerType = (marker.type || '').toUpperCase();
    if (!markerType) return null;

    for (const { layer, predicate, overrides } of candidates) {
        if ((layer.objectType || '').toUpperCase() !== markerType) continue;
        if (predicate(marker)) {
            return overrides;
        }
    }

    return null;
}

/**
 * For a single marker, find the highest-precedence enabled custom layer
 * that matches the marker's type and has a popup schema, then return
 * the derived popup overrides (or null).
 */
export function resolvePopupOverrides(marker, customLayers) {
    if (!marker || !Array.isArray(customLayers) || customLayers.length === 0) {
        return null;
    }

    const markerType = (marker.type || '').toUpperCase();
    if (!markerType) return null;

    const candidates = customLayers
        .filter((l) =>
            l.enabled
            && l.popup
            && (l.objectType || '').toUpperCase() === markerType,
        )
        .sort(layerPrecedenceComparator);

    if (candidates.length === 0) return null;

    // Pick the highest-precedence layer whose filter predicate matches
    for (const layer of candidates) {
        const predicate = compileLayerPredicate(layer.filters);
        if (predicate(marker)) {
            return buildOverridesFromPopup(layer.popup);
        }
    }

    return null;
}

/**
 * Annotate an array of markers with `_popupOverrides` from matching custom layers.
 * Precompiles predicates and overrides per layer to avoid redundant work per marker.
 * Returns a new array (markers without overrides keep their original reference).
 */
export function annotateMarkersWithPopupOverrides(markers, customLayers) {
    if (!Array.isArray(markers) || !Array.isArray(customLayers) || customLayers.length === 0) {
        return markers;
    }

    // Only consider layers that have popup schemas
    const layersWithPopup = customLayers.filter((l) => l.enabled && l.popup);
    if (layersWithPopup.length === 0) return markers;

    // Precompile predicates and overrides once per layer
    const candidates = layersWithPopup
        .sort(layerPrecedenceComparator)
        .map((layer) => ({
            layer,
            predicate: compileLayerPredicate(layer.filters),
            overrides: buildOverridesFromPopup(layer.popup),
        }))
        .filter((c) => c.overrides !== null);

    if (candidates.length === 0) return markers;

    return markers.map((marker) => {
        const overrides = resolvePopupOverridesFromCandidates(marker, candidates);
        if (!overrides) return marker;
        return { ...marker, _popupOverrides: overrides };
    });
}
